import { useCallback, useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { array, object, oneOfType } from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import ScriptLoader from 'yoda-core-components/lib/helpers/ScriptLoader/ScriptLoader';
import {
    shopTheImageItemClick,
    shopTheImageLoadSuccess,
    shopTheImageModuleClick,
} from '../../actions/AnalyticsAction';
import { miniPDPOnShopTheImageOpen } from '../../actions/MiniPDPAction';

const ShopTheImage = ({ actions }) => {
    const {
        context: {
            messagesTexts: { shopTheImageWidget: { promoCodeText = '' } = {} } = {},
            preferences: { shopTheImageWidget: { scriptURL = '' } = {} } = {},
        } = {},
    } = useSelector((store) => store);
    const [isScriptLoaded, setIsScriptLoaded] = useState(false);

    /* istanbul ignore next */
    const handleShopTheImageModuleClick = () => {
        const dialogElement = document.querySelector('.stylitics-ui-modal');
        const handleButtonClick = () => {
            document.body.style.overflow = 'auto';
        };

        if (dialogElement) {
            document.body.style.overflow = 'hidden';
            setTimeout(() => {
                const closeButton = document.querySelector('.stylitics-ui-close');
                if (closeButton) {
                    closeButton.addEventListener('click', handleButtonClick);
                }
            }, 1000);
        }
    };

    /* istanbul ignore next */
    const itemPricingOverride = (data) => {
        const { item } = data;
        if (item.promotions.includes('with_code') && item.formattedSalePrice) {
            const text = document.createElement('span');
            text.classList.add('t-red');
            text.style.marginLeft = '2px';
            text.innerHTML = promoCodeText;
            const priceContainer = data.element.querySelector('.stylitics-ui-price');
            const salePrice = priceContainer.querySelector('.stylitics-item-sale-price');
            salePrice.append(text);
            salePrice.classList.add('FPAC-price');
        }
    };

    /* istanbul ignore next */
    const onShopTheImageClicked = useCallback((item, outfit) => {
        const skuData = item?.remote_id || '';
        const itemId = item?.item_id || '';
        const skuID = item?.other_client_item_ids?.sku_nbrs[0] || '';
        /* istanbul ignore next */
        if (!isEmpty(skuData)) {
            const options = {
                ppId: skuData.split('_')[0],
                skuId: skuID,
            };
            const customEvent = document.createEvent('CustomEvent');
            customEvent.initCustomEvent('miniPDP.open', true, true, options);
            document.dispatchEvent(customEvent);
            actions.miniPDPOnShopTheImageOpen(itemId);
            actions.shopTheImageItemClick &&
                actions.shopTheImageItemClick({
                    shopTheImageID: outfit?.id?.toString() || '',
                    shopTheImagePPID: options.ppId,
                    moduleName: 'shop the image',
                });
        }
    }, []);

    /* istanbul ignore next */
    const widgetOptions = {
        src: scriptURL,
        async: true,
        defer: false,
        onSuccess: async () => {
            const widgetInstance = new StyliticsShopSimilarWidget('jcpenney', {
                api: {
                    env: 'production',
                },
                display: {
                    dialogAsModal: false,
                    shopButtonMargin: 8,
                },
            });
            widgetInstance.on('mount', 'collection', (collection) => {
                window && collection && actions.shopTheImageLoadSuccess(window.digitalData);
            });
            widgetInstance.on('click', 'collection', (collection) => {
                actions.shopTheImageModuleClick({
                    shopTheImageID: collection?.id?.toString() || '',
                });
                handleShopTheImageModuleClick();
            });
            widgetInstance.on('mount', 'item', (data) => itemPricingOverride(data));
            widgetInstance.override('click', 'item', ({ item, outfit }) => {
                onShopTheImageClicked(item, outfit);
            });
            setIsScriptLoaded(true);
            widgetInstance.start();
        },
    };

    useEffect(() => {
        if (!isScriptLoaded) {
            ScriptLoader.load(widgetOptions);
        }
    }, []);

    useEffect(() => {
        const styliticsStyleOverride = document.createElement('style');

        styliticsStyleOverride.innerHTML = `
            .stylitics-ui-modal {
                z-index: 98 !important;
            }
            .stylitics-shop-similar-modal-heading {
                color: #202020 !important;
                font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important;
                font-size: 16px !important;
            }
            .stylitics-shop-similar-items-heading {
                color: #202020 !important;
                font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important;
                font-size: 14px !important;
                line-height: 24px !important;
                margin: 0px !important;
                padding: 8px !important;
            }
            .stylitics-shop-similar-item-info {
                gap: 8px !important;
            }
            .stylitics-shop-similar-item-name {
                color: #202020 !important;
                font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important;
                font-size: 12px !important;
                line-height: 16px !important;
                min-height: 45px !important;
                padding-top: 12px !important;
            }
            .stylitics-ui-modal .stylitics-ui-carousel-outer {
                height: 350px !important;
            }
            .stylitics-ui-price {
                font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important;
                font-size: 12px !important;
                line-height: 16px !important;
            }
            .stylitics-item-regular-price {
                color: #202020 !important;
                font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important;
                font-size: 12px !important;
                font-weight: 700 !important;
                line-height: 16px !important;
            }
            .stylitics-ui-regular-price-slashed {
                color: #666 !important;
                font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important;
                font-size: 12px !important;
                font-weight: 400 !important;
                line-height: 16px !important;
            }
            .stylitics-item-sale-price {
                color: #202020 !important;
                font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important;
                font-size: 12px !important;
                font-weight: 700 !important;
                line-height: 16px !important;
            }
            .FPAC-price {
                color: #E71324 !important;
                font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important;
                font-size: 12px !important;
                font-weight: 700 !important;
                line-height: 16px !important;
            }
            .t-red {
                color: #E71324 !important;
                font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important;
                font-size: 12px !important;
                font-style: italic !important;
                font-weight: 400 !important;
                line-height: 16px !important;
            }
            .stylitics-shop-similar-item-cta {
                border-color: #999 !important;
                border-radius: 8px !important;
                color: #202020 !important;
                font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important;
                font-weight: 600 !important;
                line-height: 16px !important;
                height: 36px;
                padding: 8px 16px !important;
            }
            @media (max-width: 768px) {
                .stylitics-ui-modal[open].stylitics-ui-modal-non-modal {
                    padding-bottom: 100px !important;
                }
            }
        `;

        document.head.appendChild(styliticsStyleOverride);

        return () => {
            document.head.removeChild(styliticsStyleOverride);
        };
    }, []);

    return null;
};

ShopTheImage.defaultProps = {
    actions: {},
};

ShopTheImage.propTypes = {
    actions: oneOfType([array, object]),
};

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(
        {
            shopTheImageItemClick,
            shopTheImageLoadSuccess,
            shopTheImageModuleClick,
            miniPDPOnShopTheImageOpen,
        },
        dispatch
    ),
});

export default connect(null, mapDispatchToProps)(ShopTheImage);
